/**
 * 원격지원 파일 주고받기 엔진 — 직원 PC와 담당자 원격 창이 같은 코드를 쓴다
 *
 *  - 담당자 → 직원: 담당자 sendFiles → offer → 직원 동의(decideOffer) → 전송 → 직원 PC 다운로드 폴더
 *  - 직원 → 담당자: 담당자 requestPull → pull → 직원 동의 + 파일 선택(decidePull) → offer{pullId} → 담당자가 자동 수락
 *    (담당자는 자기가 연 pull에 대한 offer만 받는다 — 직원이 먼저 보내는 경로는 없다)
 *  - 흐름 제어: 보내는 쪽은 '받는 쪽이 디스크에 썼다고 확인(ack)하지 않은 바이트 ≤ FX_WINDOW' 이면서
 *    'bufferedAmount ≤ FX_BUFFER_HIGH'일 때만 보낸다. 받는 쪽 onmessage는 흐름을 제어할 수 없어서 ack 창이 메모리 상한이다
 *  - 방향마다 한 묶음씩만 진행한다. 채널이 닫히거나 dispose되면 진행 중인 묶음을 전부 버리고 부분 파일을 지운다(싱크 abort)
 *
 * ⚠ 의존성은 전부 주입받는다(채널·싱크·동의) — scripts/test-remote-file.js가 가짜 채널 2개를 이어 루프백으로 검증한다.
 *   그래서 이 파일은 프로토콜 모듈 말고는 import하지 않는다.
 */
import {
  FX_BUFFER_HIGH,
  FX_BUFFER_LOW,
  FX_CHUNK,
  FX_CONSENT_MS,
  FX_PULL_MS,
  FX_READ_BLOCK,
  FX_WINDOW,
  FX_WRITE_BATCH,
  FxFileMeta,
  FxMessage,
  FxRole,
  checkOutgoing,
  cleanFileName,
  createRateMeter,
  decodeChunk,
  encodeChunk,
  firstId,
  isIdOf,
  parseFxMessage,
} from '../utils/remoteFileProtocol';

/** RTCDataChannel 중 엔진이 쓰는 부분 */
export interface FxChannel {
  readonly readyState: string;
  readonly bufferedAmount: number;
  bufferedAmountLowThreshold: number;
  binaryType?: string;
  send(data: string | ArrayBuffer): void;
  addEventListener(type: string, listener: (ev: any) => void): void;
  removeEventListener(type: string, listener: (ev: any) => void): void;
}

/** 보낼 파일 — File/Blob과 같은 모양 */
export interface FxSource {
  name: string;
  size: number;
  slice(start: number, end: number): { arrayBuffer(): Promise<ArrayBuffer> };
}

/** 받은 파일을 쓰는 곳 — Rust(다운로드 폴더) 또는 Blob(웹) */
export interface FxSink {
  write(data: Uint8Array): Promise<void>;
  finish(): Promise<{ savedAs: string; revealId?: string }>;
  abort(): Promise<void>;
}

export type FxDirection = 'out' | 'in';
export type FxBatchStatus = 'waiting' | 'active' | 'done' | 'declined' | 'canceled' | 'failed';
export type FxFileStatus = 'pending' | 'active' | 'done' | 'failed' | 'canceled';

export interface FxFileState {
  fid: number;
  name: string;
  size: number;
  /** 보낸 쪽: 받는 쪽이 확인한 바이트 / 받는 쪽: 디스크에 쓴 바이트 */
  bytes: number;
  status: FxFileStatus;
  /** 받는 쪽이 실제로 저장한 이름(겹치면 'setup (1).exe') */
  savedAs?: string;
  /** 받은 쪽에서 '폴더 열기'에 쓸 핸들(Rust) 또는 blob URL(웹) */
  revealId?: string;
}

export interface FxBatchState {
  bid: number;
  direction: FxDirection;
  status: FxBatchStatus;
  files: FxFileState[];
  totalBytes: number;
  doneBytes: number;
  /** 바이트/초 */
  rate: number;
  reason?: string;
  /** 담당자가 요청(pull)해서 생긴 묶음 */
  pullId?: number;
  createdAt: number;
  endedAt?: number;
}

export interface FxSnapshot {
  open: boolean;
  batches: FxBatchState[];
  /** 담당자: 직원에게 파일을 요청하고 기다리는 중 */
  pendingPull: boolean;
}

export interface RsFileTransferOptions {
  role: FxRole;
  channel: FxChannel;
  openSink: (file: FxFileMeta, batch: FxBatchState) => Promise<FxSink>;
  /** 받을지 묻는다(직원: 동의 모달 + 서버 허가). 담당자는 엔진이 pull 일치 여부로 직접 판정한다 */
  decideOffer?: (batch: FxBatchState) => Promise<boolean>;
  /** 직원만: 파일 요청을 받았다 — 동의 + 파일 선택. null이면 거절 */
  decidePull?: (pid: number) => Promise<FxSource[] | null>;
  onChange?: (snap: FxSnapshot) => void;
  /** 묶음이 끝났다(성공·거절·취소·실패 모두) — 직원 PC가 서버에 결과를 보고한다 */
  onBatchEnd?: (batch: FxBatchState) => void;
  now?: () => number;
}

interface OutBatch {
  state: FxBatchState;
  sources: Map<number, FxSource>;
  answer: { resolve: (accept: boolean) => void } | null;
  canceled: boolean;
}

interface InBatch {
  state: FxBatchState;
  current: { fid: number; sink: FxSink | null; buf: Uint8Array[]; bufBytes: number } | null;
  canceled: boolean;
}

type RxItem = { kind: 'begin'; fid: number } | { kind: 'data'; fid: number; data: Uint8Array } | { kind: 'end'; fid: number };

const TERMINAL: FxBatchStatus[] = ['done', 'declined', 'canceled', 'failed'];
const KEEP_BATCHES = 20;
const EMIT_INTERVAL_MS = 200;

/** 사유 → 끝난 상태. 사람이 멈춘 것·연결이 끊긴 것은 '취소', 쓰기·읽기 오류는 '실패' */
function statusForReason(reason: string): FxBatchStatus {
  if (reason === 'declined' || reason === 'busy') return 'declined';
  if (['canceled', 'timeout', 'policy_off', 'disconnected', 'server_denied'].includes(reason)) return 'canceled';
  return 'failed';
}

export class RsFileTransfer {
  private readonly role: FxRole;
  private readonly ch: FxChannel;
  private readonly opts: RsFileTransferOptions;
  private readonly now: () => number;
  private nextId: number;
  private batches: FxBatchState[] = [];
  private out: OutBatch | null = null;
  private inb: InBatch | null = null;
  /** 받는 쪽 처리 대기열 — 싱크 쓰기가 끝나기 전에 다음 청크가 와도 순서대로 처리한다 */
  private rx: RxItem[] = [];
  private rxRunning = false;
  /** 보내는 쪽 대기(창·버퍼) — ack·bufferedamountlow·취소가 깨운다 */
  private wake: (() => void) | null = null;
  private acked = new Map<number, number>();
  private doneWaiters = new Map<number, (r: { ok: boolean; savedAs?: string }) => void>();
  private pendingPull: { pid: number; timer: ReturnType<typeof setTimeout> } | null = null;
  /** 직원: 처리 중인 파일 요청 */
  private servingPull = false;
  private disposed = false;
  private emitTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: RsFileTransferOptions) {
    this.role = opts.role;
    this.ch = opts.channel;
    this.opts = opts;
    this.now = opts.now ?? (() => Date.now());
    this.nextId = firstId(this.role);
    try {
      this.ch.binaryType = 'arraybuffer';
    } catch {
      /* 가짜 채널 */
    }
    this.ch.bufferedAmountLowThreshold = FX_BUFFER_LOW;
    this.ch.addEventListener('message', this.onMessage);
    this.ch.addEventListener('bufferedamountlow', this.onLow);
    this.ch.addEventListener('open', this.onOpen);
    this.ch.addEventListener('close', this.onClose);
  }

  // ─────────────── 공개 API ───────────────

  isOpen(): boolean {
    return !this.disposed && this.ch.readyState === 'open';
  }

  /** 보내는 묶음이 진행 중인가(담당자 '파일 보내기' 버튼 비활성) */
  isSending(): boolean {
    return !!this.out;
  }

  /** 받는 묶음이 진행 중이거나 요청을 기다리는 중인가(담당자 '파일 가져오기' 버튼 비활성) */
  isReceiving(): boolean {
    return !!this.inb || !!this.pendingPull;
  }

  snapshot(): FxSnapshot {
    return {
      open: this.isOpen(),
      batches: this.batches.map((b) => ({ ...b, files: b.files.map((f) => ({ ...f })) })),
      pendingPull: !!this.pendingPull,
    };
  }

  /**
   * 파일을 보낸다(담당자 → 직원). 직원 PC의 응답(pull)도 내부적으로 이 경로를 쓴다.
   * @returns 시작했으면 true. 검사 실패·이미 보내는 중·채널 닫힘이면 사유 문자열
   */
  sendFiles(sources: FxSource[], pullId?: number): true | 'busy' | 'closed' | 'invalid' {
    if (!this.isOpen()) return 'closed';
    if (this.out) return 'busy';
    if (!checkOutgoing(sources).ok) return 'invalid';
    const files: FxFileState[] = sources.map((s) => ({
      fid: this.allocId(),
      name: cleanFileName(s.name) as string,
      size: s.size,
      bytes: 0,
      status: 'pending',
    }));
    const state: FxBatchState = {
      bid: this.allocId(),
      direction: 'out',
      status: 'waiting',
      files,
      totalBytes: files.reduce((a, f) => a + f.size, 0),
      doneBytes: 0,
      rate: 0,
      pullId,
      createdAt: this.now(),
    };
    const batch: OutBatch = {
      state,
      sources: new Map(files.map((f, i) => [f.fid, sources[i]])),
      answer: null,
      canceled: false,
    };
    this.out = batch;
    this.addBatch(state);
    void this.runOut(batch);
    return true;
  }

  /** 담당자: 직원 PC의 파일을 요청한다 */
  requestPull(): true | 'busy' | 'closed' | 'not_operator' {
    if (this.role !== 'operator') return 'not_operator';
    if (!this.isOpen()) return 'closed';
    if (this.pendingPull || this.inb) return 'busy';
    const pid = this.allocId();
    const timer = setTimeout(() => {
      if (this.pendingPull?.pid === pid) this.endPull('timeout');
    }, FX_PULL_MS);
    this.pendingPull = { pid, timer };
    this.send({ t: 'pull', pid });
    this.emit(true);
    return true;
  }

  /** 담당자: 요청을 거둔다(직원 쪽 창은 직원이 닫거나 만료로 닫힌다) */
  cancelPull() {
    if (this.pendingPull) this.endPull('canceled');
  }

  /** 요청이 파일 없이 끝났다 — 진행 패널에 한 줄로 남긴다(거절·만료·취소) */
  private endPull(reason: string) {
    const p = this.pendingPull;
    if (!p) return;
    clearTimeout(p.timer);
    this.pendingPull = null;
    const t = this.now();
    this.addBatch({
      bid: p.pid,
      direction: 'in',
      status: statusForReason(reason),
      files: [],
      totalBytes: 0,
      doneBytes: 0,
      rate: 0,
      reason,
      pullId: p.pid,
      createdAt: t,
      endedAt: t,
    });
  }

  /** 진행 중인 묶음을 취소한다(어느 방향이든) */
  cancel(bid: number) {
    if (this.out?.state.bid === bid) this.cancelOut(this.out, 'canceled', true);
    else if (this.inb?.state.bid === bid) void this.cancelIn(this.inb, 'canceled', true);
  }

  /** 진행 중인 것을 전부 멈춘다(정책 꺼짐). 채널은 그대로 둔다 */
  cancelAll(reason: string) {
    if (this.out) this.cancelOut(this.out, reason, true);
    if (this.inb) void this.cancelIn(this.inb, reason, true);
    if (this.pendingPull) this.endPull(reason);
  }

  /** 모두 버린다(세션 종료). 부분 파일은 싱크 abort가 지운다 */
  dispose(reason = 'disconnected') {
    if (this.disposed) return;
    if (this.out) this.cancelOut(this.out, reason, this.ch.readyState === 'open');
    if (this.inb) void this.cancelIn(this.inb, reason, this.ch.readyState === 'open');
    if (this.pendingPull) {
      clearTimeout(this.pendingPull.timer);
      this.pendingPull = null;
    }
    this.disposed = true;
    this.ch.removeEventListener('message', this.onMessage);
    this.ch.removeEventListener('bufferedamountlow', this.onLow);
    this.ch.removeEventListener('open', this.onOpen);
    this.ch.removeEventListener('close', this.onClose);
    this.wakeUp();
    if (this.emitTimer) clearTimeout(this.emitTimer);
    this.emitTimer = null;
    this.opts.onChange?.(this.snapshot());
  }

  /** 끝난 묶음을 목록에서 지운다(진행 패널의 '닫기') */
  dismiss(bid: number) {
    const before = this.batches.length;
    this.batches = this.batches.filter((b) => b.bid !== bid || !TERMINAL.includes(b.status));
    if (this.batches.length !== before) this.emit(true);
  }

  // ─────────────── 보내는 쪽 ───────────────

  private async runOut(b: OutBatch) {
    const st = b.state;
    this.send({ t: 'offer', bid: st.bid, files: st.files.map(({ fid, name, size }) => ({ fid, name, size })), ...(st.pullId ? { pullId: st.pullId } : {}) });
    let timer: ReturnType<typeof setTimeout> | null = null;
    const accepted = await new Promise<boolean>((resolve) => {
      b.answer = { resolve };
      // 받는 쪽의 동의 시간 + 여유. 그 사이 답이 없으면 취소
      timer = setTimeout(() => {
        if (!b.answer) return;
        b.answer = null;
        resolve(false);
        if (!b.canceled && st.status === 'waiting') this.cancelOut(b, 'timeout', true);
      }, FX_CONSENT_MS + 15_000);
    });
    if (timer) clearTimeout(timer);
    if (b.canceled || this.disposed) return;
    if (!accepted) {
      this.finishOut(b, 'declined', 'declined');
      return;
    }
    st.status = 'active';
    this.emit(true);
    const meter = createRateMeter();
    for (const f of st.files) {
      if (b.canceled || this.disposed) return;
      const src = b.sources.get(f.fid);
      if (!src) continue;
      f.status = 'active';
      this.emit(true);
      const result = await this.streamFile(b, f, src, meter);
      if (b.canceled || this.disposed) return;
      if (result.ok === false) {
        f.status = 'failed';
        this.cancelOut(b, result.reason, true);
        return;
      }
      f.status = 'done';
      f.bytes = f.size;
      f.savedAs = result.savedAs;
      this.recountOut(st, meter);
      this.emit(true);
    }
    this.finishOut(b, 'done');
  }

  private async streamFile(
    b: OutBatch,
    f: FxFileState,
    src: FxSource,
    meter: ReturnType<typeof createRateMeter>
  ): Promise<{ ok: true; savedAs?: string } | { ok: false; reason: string }> {
    const fid = f.fid;
    this.acked.set(fid, 0);
    const done = new Promise<{ ok: boolean; savedAs?: string }>((resolve) => this.doneWaiters.set(fid, resolve));
    this.send({ t: 'begin', fid });
    let offset = 0;
    try {
      while (offset < f.size) {
        // 창·버퍼가 찰 때까지 기다린다
        while (
          !b.canceled &&
          !this.disposed &&
          (offset - (this.acked.get(fid) ?? 0) >= FX_WINDOW || this.ch.bufferedAmount > FX_BUFFER_HIGH)
        ) {
          await new Promise<void>((r) => {
            this.wake = r;
          });
        }
        if (b.canceled || this.disposed) return { ok: false, reason: 'canceled' };
        const end = Math.min(f.size, offset + FX_READ_BLOCK);
        let block: Uint8Array;
        try {
          block = new Uint8Array(await src.slice(offset, end).arrayBuffer());
        } catch {
          return { ok: false, reason: 'read_failed' };
        }
        if (block.byteLength !== end - offset) return { ok: false, reason: 'read_failed' };
        if (b.canceled || this.disposed) return { ok: false, reason: 'canceled' };
        for (let p = 0; p < block.byteLength; p += FX_CHUNK) {
          this.ch.send(encodeChunk(fid, block.subarray(p, Math.min(block.byteLength, p + FX_CHUNK))));
        }
        offset = end;
        this.recountOut(b.state, meter);
      }
      this.send({ t: 'end', fid });
    } catch {
      // send가 throw = 채널이 닫혔다
      return { ok: false, reason: 'disconnected' };
    }
    const r = await done;
    this.doneWaiters.delete(fid);
    if (!r.ok) return { ok: false, reason: 'write_failed' };
    return { ok: true, savedAs: r.savedAs };
  }

  private recountOut(st: FxBatchState, meter: ReturnType<typeof createRateMeter>) {
    st.doneBytes = st.files.reduce((a, f) => a + (f.status === 'done' ? f.size : f.status === 'active' ? f.bytes : 0), 0);
    meter.push(st.doneBytes, this.now());
    st.rate = meter.rate();
  }

  private cancelOut(b: OutBatch, reason: string, notify: boolean) {
    if (b.canceled) return;
    b.canceled = true;
    if (notify) this.send({ t: 'cancel', bid: b.state.bid, reason });
    if (b.answer) {
      const a = b.answer;
      b.answer = null;
      a.resolve(false);
    }
    for (const f of b.state.files) {
      const w = this.doneWaiters.get(f.fid);
      if (w) {
        this.doneWaiters.delete(f.fid);
        w({ ok: false });
      }
      if (f.status === 'active' || f.status === 'pending') f.status = 'canceled';
    }
    this.wakeUp();
    this.finishOut(b, statusForReason(reason), reason);
  }

  private finishOut(b: OutBatch, status: FxBatchStatus, reason?: string) {
    if (this.out !== b) return;
    this.out = null;
    this.endBatch(b.state, status, reason);
  }

  // ─────────────── 받는 쪽 ───────────────

  private async onOffer(m: Extract<FxMessage, { t: 'offer' }>) {
    const peer: FxRole = this.role === 'host' ? 'operator' : 'host';
    if (!isIdOf(peer, m.bid) || m.files.some((f) => !isIdOf(peer, f.fid))) return;
    // 담당자는 자기가 연 요청에 대한 응답만 받는다 / 직원은 요청 응답(pullId)을 받을 일이 없다
    if (this.role === 'operator') {
      if (!this.pendingPull || m.pullId !== this.pendingPull.pid) {
        this.send({ t: 'answer', bid: m.bid, accept: false });
        return;
      }
      clearTimeout(this.pendingPull.timer);
      this.pendingPull = null;
    } else if (m.pullId !== undefined) {
      this.send({ t: 'answer', bid: m.bid, accept: false });
      return;
    }
    if (this.inb) {
      this.send({ t: 'answer', bid: m.bid, accept: false });
      return;
    }
    const state: FxBatchState = {
      bid: m.bid,
      direction: 'in',
      status: 'waiting',
      files: m.files.map((f) => ({ ...f, bytes: 0, status: 'pending' as FxFileStatus })),
      totalBytes: m.files.reduce((a, f) => a + f.size, 0),
      doneBytes: 0,
      rate: 0,
      pullId: m.pullId,
      createdAt: this.now(),
    };
    const batch: InBatch = { state, current: null, canceled: false };
    this.inb = batch;
    this.addBatch(state);
    let accept = this.role === 'operator';
    if (this.role === 'host') {
      try {
        accept = this.opts.decideOffer ? await this.opts.decideOffer({ ...state }) : false;
      } catch {
        accept = false;
      }
    }
    if (batch.canceled || this.disposed) return;
    this.send({ t: 'answer', bid: m.bid, accept });
    if (!accept) {
      this.endIn(batch, 'declined', 'declined');
      return;
    }
    state.status = 'active';
    this.emit(true);
  }

  private async onPull(pid: number) {
    if (this.role !== 'host' || !isIdOf('operator', pid)) return;
    if (this.servingPull || this.out) {
      this.send({ t: 'pull-declined', pid });
      return;
    }
    this.servingPull = true;
    let sources: FxSource[] | null = null;
    try {
      sources = this.opts.decidePull ? await this.opts.decidePull(pid) : null;
    } catch {
      sources = null;
    } finally {
      this.servingPull = false;
    }
    if (this.disposed) return;
    if (!sources || sources.length === 0) {
      this.send({ t: 'pull-declined', pid });
      return;
    }
    const r = this.sendFiles(sources, pid);
    if (r !== true) this.send({ t: 'pull-declined', pid });
  }

  /** 받은 데이터·begin·end를 순서대로 처리한다(싱크 쓰기는 비동기) */
  private enqueue(item: RxItem) {
    this.rx.push(item);
    if (!this.rxRunning) void this.drainRx();
  }

  private async drainRx() {
    this.rxRunning = true;
    try {
      while (this.rx.length > 0) {
        const item = this.rx.shift() as RxItem;
        const b = this.inb;
        if (!b || b.canceled || b.state.status !== 'active') continue;
        const f = b.state.files.find((x) => x.fid === item.fid);
        if (!f) continue;
        try {
          if (item.kind === 'begin') {
            if (b.current || f.status !== 'pending') throw new Error('protocol');
            f.status = 'active';
            b.current = { fid: f.fid, sink: null, buf: [], bufBytes: 0 };
            this.emit(true);
            const sink = await this.opts.openSink({ fid: f.fid, name: f.name, size: f.size }, { ...b.state });
            if (b.canceled || this.disposed) {
              await sink.abort().catch(() => {});
              continue;
            }
            b.current.sink = sink;
          } else if (item.kind === 'data') {
            const cur = b.current;
            if (!cur || cur.fid !== f.fid || !cur.sink) throw new Error('protocol');
            if (f.bytes + cur.bufBytes + item.data.byteLength > f.size) throw new Error('size_mismatch');
            cur.buf.push(item.data);
            cur.bufBytes += item.data.byteLength;
            if (cur.bufBytes >= FX_WRITE_BATCH) await this.flushIn(b, f);
          } else {
            const cur = b.current;
            if (!cur || cur.fid !== f.fid || !cur.sink) throw new Error('protocol');
            await this.flushIn(b, f);
            if (b.canceled) continue;
            if (f.bytes !== f.size) throw new Error('size_mismatch');
            const saved = await cur.sink.finish();
            b.current = null;
            f.status = 'done';
            f.savedAs = saved.savedAs;
            f.revealId = saved.revealId;
            this.send({ t: 'done', fid: f.fid, ok: true, savedAs: cleanFileName(saved.savedAs) ?? undefined });
            this.recountIn(b.state);
            this.emit(true);
            if (b.state.files.every((x) => x.status === 'done')) this.endIn(b, 'done');
          }
        } catch (err) {
          const msg = String((err as Error)?.message || err);
          const reason = msg.includes('disk_full') ? 'disk_full' : msg.includes('size_mismatch') ? 'size_mismatch' : 'write_failed';
          f.status = 'failed';
          this.send({ t: 'done', fid: f.fid, ok: false });
          await this.cancelIn(b, reason, true);
        }
      }
    } finally {
      this.rxRunning = false;
    }
  }

  private inMeter = createRateMeter();

  private async flushIn(b: InBatch, f: FxFileState) {
    const cur = b.current;
    if (!cur || !cur.sink || cur.bufBytes === 0) return;
    const merged = new Uint8Array(cur.bufBytes);
    let p = 0;
    for (const part of cur.buf) {
      merged.set(part, p);
      p += part.byteLength;
    }
    cur.buf = [];
    cur.bufBytes = 0;
    await cur.sink.write(merged);
    if (b.canceled) return;
    f.bytes += merged.byteLength;
    this.send({ t: 'ack', fid: f.fid, bytes: f.bytes });
    this.recountIn(b.state);
    this.emit(false);
  }

  private recountIn(st: FxBatchState) {
    st.doneBytes = st.files.reduce((a, f) => a + f.bytes, 0);
    this.inMeter.push(st.doneBytes, this.now());
    st.rate = this.inMeter.rate();
  }

  private async cancelIn(b: InBatch, reason: string, notify: boolean) {
    if (b.canceled) return;
    b.canceled = true;
    if (notify) this.send({ t: 'cancel', bid: b.state.bid, reason });
    for (const f of b.state.files) {
      if (f.status === 'active' || f.status === 'pending') f.status = 'canceled';
    }
    const sink = b.current?.sink;
    b.current = null;
    this.endIn(b, statusForReason(reason), reason);
    if (sink) await sink.abort().catch(() => {});
  }

  private endIn(b: InBatch, status: FxBatchStatus, reason?: string) {
    if (this.inb !== b) return;
    this.inb = null;
    this.inMeter = createRateMeter();
    this.endBatch(b.state, status, reason);
  }

  // ─────────────── 메시지 ───────────────

  private onMessage = (ev: { data: unknown }) => {
    if (this.disposed) return;
    const data = ev.data;
    if (data instanceof ArrayBuffer) {
      const c = decodeChunk(data);
      if (c) this.enqueue({ kind: 'data', fid: c.fid, data: c.data });
      return;
    }
    const m = parseFxMessage(data);
    if (!m) return;
    switch (m.t) {
      case 'offer':
        void this.onOffer(m);
        break;
      case 'answer': {
        const b = this.out;
        if (b && b.state.bid === m.bid && b.answer) {
          const a = b.answer;
          b.answer = null;
          a.resolve(m.accept);
        }
        break;
      }
      case 'pull':
        void this.onPull(m.pid);
        break;
      case 'pull-declined':
        if (this.pendingPull?.pid === m.pid) this.endPull('declined');
        break;
      case 'begin':
      case 'end':
        this.enqueue({ kind: m.t, fid: m.fid });
        break;
      case 'ack': {
        const b = this.out;
        const f = b?.state.files.find((x) => x.fid === m.fid);
        if (!b || !f) break;
        if (m.bytes > (this.acked.get(m.fid) ?? 0) && m.bytes <= f.size) {
          this.acked.set(m.fid, m.bytes);
          f.bytes = m.bytes;
          this.emit(false);
        }
        this.wakeUp();
        break;
      }
      case 'done': {
        const w = this.doneWaiters.get(m.fid);
        if (w) {
          this.doneWaiters.delete(m.fid);
          w({ ok: m.ok, savedAs: m.savedAs });
        }
        break;
      }
      case 'cancel':
        if (this.out?.state.bid === m.bid) this.cancelOut(this.out, m.reason, false);
        else if (this.inb?.state.bid === m.bid) void this.cancelIn(this.inb, m.reason, false);
        break;
    }
  };

  private onLow = () => this.wakeUp();

  private onOpen = () => this.emit(true);

  private onClose = () => this.dispose('disconnected');

  // ─────────────── 공통 ───────────────

  private send(m: FxMessage) {
    if (this.ch.readyState !== 'open') return;
    try {
      this.ch.send(JSON.stringify(m));
    } catch {
      /* 닫히는 중 — close 이벤트가 정리한다 */
    }
  }

  private wakeUp() {
    const w = this.wake;
    this.wake = null;
    w?.();
  }

  private allocId(): number {
    const id = this.nextId;
    this.nextId += 2;
    return id;
  }

  private addBatch(st: FxBatchState) {
    this.batches.push(st);
    // 끝난 묶음은 최근 것만 남긴다
    const ended = this.batches.filter((b) => TERMINAL.includes(b.status));
    if (ended.length > KEEP_BATCHES) {
      const drop = new Set(ended.slice(0, ended.length - KEEP_BATCHES));
      this.batches = this.batches.filter((b) => !drop.has(b));
    }
    this.emit(true);
  }

  private endBatch(st: FxBatchState, status: FxBatchStatus, reason?: string) {
    if (TERMINAL.includes(st.status)) return;
    st.status = status;
    if (reason && status !== 'done') st.reason = reason;
    st.endedAt = this.now();
    st.rate = 0;
    this.emit(true);
    try {
      this.opts.onBatchEnd?.({ ...st, files: st.files.map((f) => ({ ...f })) });
    } catch (err) {
      console.warn('[RemoteFile] onBatchEnd failed:', err);
    }
  }

  /** 상태 변화는 즉시, 진행률은 EMIT_INTERVAL_MS로 묶어서 알린다 */
  private emit(immediate: boolean) {
    if (!this.opts.onChange) return;
    if (immediate) {
      if (this.emitTimer) {
        clearTimeout(this.emitTimer);
        this.emitTimer = null;
      }
      this.opts.onChange(this.snapshot());
      return;
    }
    if (this.emitTimer) return;
    this.emitTimer = setTimeout(() => {
      this.emitTimer = null;
      if (!this.disposed) this.opts.onChange?.(this.snapshot());
    }, EMIT_INTERVAL_MS);
  }
}
